import { store, game } from "../state/index.ts";
import { formatMove } from "./utils.js";
import * as api from "../services/api.ts";
import * as config from "./config.js";
import { applyMove, updateBookOptions, syncBoard } from "./board.js";
import { lookup, toExplorerMoves } from "../domain/catalogTree.ts";

// DOM elements
let bookMovesList = null;
let myMovesList = null;
let userTurnMessage = null;
let userLevelMovesColorSpan = null;

// Callbacks
let onEvaluatePosition = null;
let onSparringMove = null;
let onAutoPlay = null;

// Position tree of the catalog line being practiced, when there is one
let treeProvider = null;

// Only the answer for the position currently on the board may be applied
let loadToken = 0;
let myMovesToken = 0;

export function initBook(elements, callbacks = {}) {
    bookMovesList = elements.bookMovesList;
    myMovesList = elements.myMovesList;
    userTurnMessage = elements.userTurnMessage;
    userLevelMovesColorSpan = elements.userLevelMovesColorSpan;
    onEvaluatePosition = callbacks.onEvaluatePosition;
    onSparringMove = callbacks.onSparringMove;
    onAutoPlay = callbacks.onAutoPlay;
}

export function setTreeProvider(provider) {
    treeProvider = provider;
}

function setListMessage(list, message) {
    if (!list) return;
    list.innerHTML = `<li>${message}</li>`;
}

function renderMoveList(element, moves) {
    if (!element) return;
    element.innerHTML = "";
    if (!moves || !moves.length) {
        element.innerHTML = "<li>No moves found for this filter.</li>";
        return;
    }
    const totalPool = moves.reduce((sum, m) => sum + m.white + m.black + m.draws, 0);
    moves.slice(0, 10).forEach((move) => {
        const li = document.createElement("li");
        li.innerHTML = formatMove(move, totalPool);
        li.addEventListener("click", () => applyBookMove(move));
        element.appendChild(li);
    });
}

function renderBookMoves(payload, forceShow = false) {
    const moves = payload?.moves || [];
    const userSide = config.getSelectedBoardSide()[0]; // 'w' or 'b'

    // Update the color text in the button
    if (userLevelMovesColorSpan) {
        userLevelMovesColorSpan.textContent = userSide === "w" ? "White" : "Black";
    }

    // Always show sparring moves regardless of whose turn it is
    if (userTurnMessage) userTurnMessage.classList.add("hidden");
    store.batch((set) => {
        set('book.latestMoves', moves);
        set('book.stats', computeBookStats(moves));
    });
    renderMoveList(bookMovesList, moves);
    updateBookOptions();
}

function showBookLoadError(message, errorCode = null) {
    store.batch((set) => {
        set('book.latestMoves', []);
        set('book.stats', null);
        set('book.error', errorCode);
    });
    setListMessage(bookMovesList, message);
    updateBookOptions();
}

export function pickWeightedMove(moves) {
    const weights = moves.map((m) => m.white + m.black + m.draws);
    const total = weights.reduce((sum, n) => sum + n, 0);
    if (total === 0) {
        console.log("[sparring] No games in pool, picking first move:", moves[0]?.san);
        return moves[0];
    }
    const roll = Math.random() * total;
    let threshold = roll;
    for (let i = 0; i < moves.length; i++) {
        threshold -= weights[i];
        if (threshold <= 0) {
            const pct = ((weights[i] / total) * 100).toFixed(1);
            console.log(`[sparring] Picked move ${moves[i].san} (index ${i}, ${pct}% probability, ${weights[i]}/${total} games, roll=${roll.toFixed(1)})`);
            return moves[i];
        }
    }
    const lastIdx = moves.length - 1;
    const pct = ((weights[lastIdx] / total) * 100).toFixed(1);
    console.log(`[sparring] Picked last move ${moves[lastIdx].san} (index ${lastIdx}, ${pct}% probability, fallback)`);
    return moves[lastIdx];
}

export function applyBookMove(move) {
    const fenBefore = game.fen();
    const applied = applyMove(move);
    if (applied) {
        if (onSparringMove) onSparringMove({ fenBefore, san: move.san, uci: move.uci });
        if (onEvaluatePosition) onEvaluatePosition();
        loadBookForCurrentPosition(false);
    }
}

/**
 * Opening book for a position: { moves, source, opening } or { error }.
 * A catalog line answers from its pre-generated tree. Otherwise it is the live Lichess explorer,
 * which only works with the user's own Lichess token - without one the request is not even made.
 */
export async function fetchBook(fen) {
    const node = lookup(treeProvider ? treeProvider() : null, fen);
    if (node) {
        return { moves: toExplorerMoves(node), source: "catalog", opening: node.op || null };
    }
    if (!store.get('user.currentUser')) return { error: "notLoggedIn" };
    if (!store.get('user.lichessConnected')) return { error: "lichessNotConnected" };

    // The panels, the practice and the sparring reply all ask for the same position within a second.
    // Lichess rate-limits the explorer, so they share one request, and good answers are kept.
    const formData = config.getFormData();
    const key = `${config.isMasterMode() ? "masters" : "lichess"}|${new URLSearchParams(formData).toString()}|${fen}`;
    if (!liveBookCache.has(key)) {
        const request = fetchLiveBook(fen, formData).then((book) => {
            if (book.error) liveBookCache.delete(key);
            return book;
        });
        liveBookCache.set(key, request);
        if (liveBookCache.size > LIVE_BOOK_CACHE_SIZE) {
            liveBookCache.delete(liveBookCache.keys().next().value);
        }
    }
    return liveBookCache.get(key);
}

const LIVE_BOOK_CACHE_SIZE = 60;
const LIVE_BOOK_RETRY_MS = 1500;
const liveBookCache = new Map();

async function fetchLiveBook(fen, formData, attempt = 1) {
    let payload = null;
    try {
        payload = config.isMasterMode()
            ? await api.fetchMastersBookMoves(fen, formData)
            : await api.fetchBookMoves(fen, formData);
    } catch (err) {
        console.error("Failed to load book moves", err);
    }
    if (!payload) {
        // Usually Lichess asking to slow down: one more try before giving up on the position
        if (attempt === 1) {
            await new Promise((resolve) => setTimeout(resolve, LIVE_BOOK_RETRY_MS));
            return fetchLiveBook(fen, formData, 2);
        }
        return { error: "loadError" };
    }
    // The stored token was revoked or expired on the Lichess side
    if (payload.error === "lichess_auth_required") return { error: "lichessNotConnected" };
    const opening = payload.opening ? `${payload.opening.eco} ${payload.opening.name}` : null;
    return { moves: payload.moves || [], source: "lichess", opening };
}

const BOOK_ERROR_MESSAGES = {
    notLoggedIn: '<a href="/login">Log in</a> and connect your Lichess account to see live opening statistics.',
    lichessNotConnected: 'Connect your <a href="/api/lichess/connect">Lichess account</a> to see live opening statistics.',
    loadError: "Failed to load book moves.",
};

export async function loadBookForCurrentPosition(autoPlay) {
    if (game.isGameOver()) {
        return;
    }
    const fen = game.fen();
    const token = ++loadToken;
    const book = await fetchBook(fen);
    // The board moved on while waiting: this answer belongs to another position
    if (token !== loadToken || game.fen() !== fen) return;

    if (book.error) {
        showBookLoadError(BOOK_ERROR_MESSAGES[book.error], book.error);
        return;
    }
    store.batch((set) => {
        set('book.error', null);
        set('book.source', book.source);
        set('book.fen', fen);
        // Kept while the book has no name for the position: a variation's name holds for what follows
        if (book.opening) set('book.opening', book.opening);
    });
    renderBookMoves(book);
    if (autoPlay && book.moves.length) {
        if (onAutoPlay) {
            onAutoPlay(book.moves, fen);
        } else {
            setTimeout(() => {
                if (game.fen() === fen) applyBookMove(pickWeightedMove(book.moves));
            });
        }
    }
}

export async function loadMyMovesForCurrentPosition() {
    // Check if user is logged in
    const currentUser = store.get('user.currentUser');
    if (!currentUser) {
        store.batch((set) => {
            set('myGames.latestMoves', []);
            set('myGames.stats', null);
            set('myGames.isLoading', false);
            set('myGames.error', 'notLoggedIn');
        });
        return;
    }

    if(myMovesList) {
      setListMessage(myMovesList, "Loading...");
    }

    store.batch((set) => {
        set('myGames.isLoading', true);
        set('myGames.error', null);
    });

    const fen = game.fen();
    const token = ++myMovesToken;
    const userSide = config.getSelectedBoardSide()[0]; // 'w' or 'b'
    const color = userSide === "w" ? "WHITE" : "BLACK";
    let moves = null;
    try {
        moves = await api.fetchPlayerNextMoves({ fen, color });
    } catch (err) {
        if (token !== myMovesToken) return;
        console.error("Failed to load user game moves", err);
        if (err.message.includes("logged in")) {
            setListMessage(myMovesList, "Log in to view your imported game moves.");
            store.batch((set) => {
                set('myGames.latestMoves', []);
                set('myGames.stats', null);
                set('myGames.isLoading', false);
                set('myGames.error', 'notLoggedIn');
            });
        } else {
            setListMessage(myMovesList, "Failed to load moves.");
            store.batch((set) => {
                set('myGames.latestMoves', []);
                set('myGames.stats', null);
                set('myGames.isLoading', false);
                set('myGames.error', null);
            });
        }
        return;
    }

    // A slow answer for a position the board has already left must not overwrite the current one
    if (token !== myMovesToken || game.fen() !== fen) return;

    renderUserMoveList(myMovesList, moves || []);
    store.batch((set) => {
        set('myGames.latestMoves', moves || []);
        set('myGames.fen', fen);
        set('myGames.stats', computeMyGamesStats(moves));
        set('myGames.isLoading', false);
        set('myGames.error', null);
    });

    // Load plans in background (non-blocking)
    loadPlansForCurrentPosition(fen, color);
}

async function loadPlansForCurrentPosition(fen, color) {
    try {
        const plans = await api.fetchPlayerPlans({ fen, color, depth: 6 });
        store.set('myGames.plans', plans || []);
    } catch (err) {
        console.error("Failed to load plans", err);
        store.set('myGames.plans', []);
    }
}

function renderUserMoveList(element, moves) {
    if (!element) return;
    element.innerHTML = "";
    if (!moves || !moves.length) {
        element.innerHTML = "<li>No moves found in your imported games for this position.</li>";
        return;
    }

    const totalPool = moves.reduce((sum, m) => sum + m.total, 0);

    moves.slice(0, 10).forEach((move) => {
        const li = document.createElement("li");
        const pct = totalPool > 0 ? ((move.total / totalPool) * 100).toFixed(1) : 0;
        const winPct = move.total > 0 ? ((move.wins / move.total) * 100).toFixed(0) : 0;
        const drawPct = move.total > 0 ? ((move.draws / move.total) * 100).toFixed(0) : 0;
        const lossPct = move.total > 0 ? ((move.losses / move.total) * 100).toFixed(0) : 0;

        li.innerHTML = `
      <div class="move-line">
        <span class="move-text">${move.move}</span>
        <span>${move.total} game${move.total === 1 ? "" : "s"} (${pct}%)</span>
      </div>
      <div class="move-subline">
        <span class="win-stat">+${winPct}%</span>
        <span class="draw-stat">=${drawPct}%</span>
        <span class="loss-stat">-${lossPct}%</span>
      </div>
    `;
        li.addEventListener("click", () => applyUserMove(move));
        element.appendChild(li);
    });
}

function computeBookStats(moves) {
    if (!moves || !moves.length) return null;
    const total = moves.reduce((sum, m) => sum + (m.white || 0) + (m.black || 0) + (m.draws || 0), 0);
    const white = moves.reduce((sum, m) => sum + (m.white || 0), 0);
    const draws = moves.reduce((sum, m) => sum + (m.draws || 0), 0);
    const black = moves.reduce((sum, m) => sum + (m.black || 0), 0);
    return {
        total, white, draws, black,
        whitePct: total > 0 ? Math.round((white / total) * 100) : 0,
        drawPct: total > 0 ? Math.round((draws / total) * 100) : 0,
        blackPct: total > 0 ? Math.round((black / total) * 100) : 0,
    };
}

function computeMyGamesStats(moves) {
    if (!moves || !moves.length) return null;
    const total = moves.reduce((sum, m) => sum + (m.total || 0), 0);
    const wins = moves.reduce((sum, m) => sum + (m.wins || 0), 0);
    const draws = moves.reduce((sum, m) => sum + (m.draws || 0), 0);
    const losses = moves.reduce((sum, m) => sum + (m.losses || 0), 0);
    return {
        total, wins, draws, losses,
        winPct: total > 0 ? Math.round((wins / total) * 100) : 0,
        drawPct: total > 0 ? Math.round((draws / total) * 100) : 0,
        lossPct: total > 0 ? Math.round((losses / total) * 100) : 0,
    };
}

function applyUserMove(move) {
    // Moves from the user's games come in UCI (e.g. "e2e4")
    const applied = applyMove({ uci: move.move });
    if (applied) {
        if (onEvaluatePosition) onEvaluatePosition();
        loadBookForCurrentPosition(false);
    }
}
